// R572 (c): raising a cash out on HOP. POST /cash-outs is R572 (a)'s route, its body as (a2)
// changes it (Rook #69117): { payeeKind, payeeText, amountUsd, reason }, where amountUsd is the
// typed text padded to two decimals ("12.50"; Rook #69141), never a JSON number. It goes through secureFetch
// directly, never the offline queue: a cash out is a deliberate write that must say raised or
// refused where it was made, so the form disables its button offline instead (as Settings does).
import { secureFetch } from './auth'
import { failureMessage } from './orders-api'

/**
 * Who sees the form and its Cockpit entry (Rook #69106 ruling 2): owner and admin. The server's
 * CASH_OUT_RAISE_ROLES (master-layer/src/drawer/drawer-roles.ts) also names accountant, but
 * HOP's own entry gate (hasCockpitAccess, COCKPIT_ROLES) admits no accountant login; that gap is
 * its own card. Roles are named one by one, so a role added later is not let in by default.
 */
export const CASH_OUT_FORM_ROLES = ['owner', 'admin'] as const

export function canRaiseCashOut(roles: readonly string[]): boolean {
  return roles.some((r) => (CASH_OUT_FORM_ROLES as readonly string[]).includes(r))
}

export const PAYEE_KINDS = ['staff', 'external'] as const
export type PayeeKind = (typeof PAYEE_KINDS)[number]

/** Dollars with at most two decimals ("12", "12.5", "12.50"); a third decimal or a letter is refused. */
export const USD_TYPED = /^\d{1,10}(\.\d{1,2})?$/

/**
 * The typed amount as it is sent, padded to two decimals ("12" is "12.00", "12.5" is "12.50";
 * Rook #69141), or null when the form must refuse it. Zero is refused.
 */
export function amountText(typed: string): string | null {
  const text = typed.trim()
  if (!USD_TYPED.test(text)) return null
  const [whole, frac = ''] = text.split('.')
  const padded = `${whole}.${frac.padEnd(2, '0')}`
  if (/^0+\.00$/.test(padded)) return null
  return padded
}

export interface RaiseCashOutBody {
  payeeKind: PayeeKind
  payeeText: string
  amountUsd: string
  reason: string
}

/** POST /cash-outs's answer (cash-outs.service.ts view()). */
export interface CashOutRequest {
  id: string
  payeeKind: string
  payeeText: string
  amountUsd: string
  reason: string
  raisedBy: string
  raisedByName: string | null
  raisedAt: string
  status: string
  paidAt: string | null
}

export class CashOutApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

export async function raiseCashOut(body: RaiseCashOutBody): Promise<CashOutRequest> {
  const res = await secureFetch('/api/cash-outs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new CashOutApiError(await failureMessage(res), res.status)
  return res.json() as Promise<CashOutRequest>
}
